
import { createTheme, alpha } from '@mui/material/styles';

// Premium Color Palette
const colors = {
    primary: {
        main: '#667eea',
        light: '#764ba2', // Gradient end
        dark: '#5a6fd6',
        contrastText: '#fff',
    },
    secondary: {
        main: '#4fd1c5',
        light: '#81e6d9',
        dark: '#38b2ac',
        contrastText: '#fff',
    },
    success: {
        main: '#10b981',
        light: '#d1fae5',
    },
    error: {
        main: '#ef4444',
        light: '#fee2e2',
    },
    warning: {
        main: '#f59e0b',
        light: '#fef3c7',
    },
    info: {
        main: '#3b82f6',
        light: '#dbeafe',
    },
    background: {
        default: '#f8f9fa',
        paper: '#ffffff',
    },
    text: {
        primary: '#1e293b',
        secondary: '#64748b',
    }
};

const glassStyles = {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.07)',
};

const muiTheme = createTheme({
    palette: colors,
    glass: glassStyles,
    typography: {
        fontFamily: "'Outfit', 'Inter', 'Poppins', sans-serif",
        h1: { fontWeight: 700, letterSpacing: '-0.02em', color: colors.text.primary },
        h2: { fontWeight: 700, letterSpacing: '-0.01em', color: colors.text.primary },
        h3: { fontWeight: 700, color: colors.text.primary },
        h4: { fontWeight: 700, color: colors.text.primary },
        h5: { fontWeight: 600, color: colors.text.primary },
        h6: { fontWeight: 600, color: colors.text.primary },
        subtitle1: { fontWeight: 500, color: colors.text.secondary },
        subtitle2: { fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.text.secondary },
        button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.02em' },
        body1: { fontSize: '1rem', lineHeight: 1.6, color: colors.text.primary },
        body2: { fontSize: '0.875rem', lineHeight: 1.6, color: colors.text.secondary },
    },
    shape: {
        borderRadius: 16,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: colors.background.default,
                    scrollbarColor: "#6b6b6b #2b2b2b",
                    "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
                        backgroundColor: "transparent",
                        width: 8,
                        height: 8,
                    },
                    "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                        borderRadius: 8,
                        backgroundColor: "#d1d5db",
                        minHeight: 24,
                        border: "2px solid transparent",
                        backgroundClip: "content-box",
                    },
                    "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "#9ca3af",
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    padding: '10px 24px',
                    boxShadow: 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                    },
                    '&:active': {
                        transform: 'translateY(0)',
                    }
                },
                containedPrimary: {
                    background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.light} 100%)`,
                    boxShadow: `0 4px 15px ${alpha(colors.primary.main, 0.4)}`,
                    '&:hover': {
                        background: `linear-gradient(135deg, ${colors.primary.dark} 0%, ${colors.primary.main} 100%)`,
                        boxShadow: `0 8px 25px ${alpha(colors.primary.main, 0.5)}`,
                    }
                },
                containedSecondary: {
                    background: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.dark} 100%)`,
                    boxShadow: `0 4px 15px ${alpha(colors.secondary.main, 0.4)}`,
                    '&:hover': {
                        boxShadow: `0 8px 25px ${alpha(colors.secondary.main, 0.5)}`,
                    }
                },
                outlined: {
                    borderWidth: 2,
                    borderColor: alpha(colors.primary.main, 0.5),
                    '&:hover': {
                        borderWidth: 2,
                        backgroundColor: alpha(colors.primary.main, 0.05),
                    }
                }
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 24,
                    ...glassStyles,
                    overflow: 'visible',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    backgroundImage: 'none',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    borderRadius: 20,
                },
                elevation0: {
                    ...glassStyles,
                },
                elevation1: {
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                },
                elevation8: {
                    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                    backdropFilter: 'blur(20px)',
                    backgroundColor: alpha('#ffffff', 0.9),
                }
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 14,
                        transition: 'all 0.2s ease',
                        backgroundColor: alpha('#ffffff', 0.8),
                        '& fieldset': {
                            borderColor: alpha('#e2e8f0', 0.8),
                        },
                        '&:hover fieldset': {
                            borderColor: colors.primary.main,
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: colors.primary.main,
                            borderWidth: 2,
                            boxShadow: `0 0 0 4px ${alpha(colors.primary.main, 0.1)}`,
                        },
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: '18px 24px',
                    borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
                    fontSize: '0.9rem',
                },
                head: {
                    fontWeight: 700,
                    backgroundColor: alpha('#f8fafc', 0.5),
                    color: '#475569',
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.08em',
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: alpha(colors.primary.main, 0.03),
                        transform: 'scale(1.002)',
                        boxShadow: 'inset 0 0 0 1px rgba(102, 126, 234, 0.1)',
                    }
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 28,
                    ...glassStyles,
                    backdropFilter: 'blur(25px)',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                },
                backdrop: {
                    backgroundColor: 'rgba(15, 23, 42, 0.2)',
                    backdropFilter: 'blur(8px)',
                }
            }
        }
    },
});

export default muiTheme;
